import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Lock, Globe2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { applyBrand } from "../lib/brand";
import { useApp } from "../context/AppContext";
import { BrandLogo, Btn, ErrorBox } from "../components/common";

const HERO = "https://images.pexels.com/photos/7658251/pexels-photo-7658251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { status, reload } = useApp();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const lgu = site?.slug || params.get("lgu") || "";

  useEffect(() => {
    api.get("/site", { params: { slug: slug || "" } }).then((r) => {
      setSite(r.data);
      if (r.data.kind === "barangay" && r.data.branding) { setB(r.data.branding); applyBrand(r.data.branding); }
      else if (["barangay", "unknown", "removed"].includes(r.data.kind)) setLoadErr(r.data.kind === "unknown" ? "This barangay site is not registered." : r.data.kind === "removed" ? "This barangay site has been removed." : "This barangay site is currently unavailable.");
      else if (params.get("lgu")) api.get("/public/branding", { params: { lgu: params.get("lgu") } }).then((x) => { setB(x.data); applyBrand(x.data); });
      else setB({ lgu_name: "Barangay Information Management System", system_name: "Provincial · City · Municipal · Barangay sign-in", address: "" });
    }).catch((e) => setLoadErr(errMsg(e)));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "authed") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post("/auth/login", { ...form, site: slug || null });
      if (r.data.otp_required) { nav("/verify-otp", { state: { branding: b, back: slug ? `/b/${slug}/login` : "/login" } }); return; }
      await reload();
      nav("/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-brand overflow-hidden" data-testid="login-brand-panel">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity" />
        <div className="relative flex items-center gap-4">
          {b && <BrandLogo branding={b} size={64} testid="login-logo" />}
          <div>
            <p className="font-display text-2xl font-bold" data-testid="login-lgu-name">{b?.lgu_name || "…"}</p>
            <p className="opacity-90" data-testid="login-system-name">{b?.system_name}</p>
          </div>
        </div>
        <div className="relative max-w-lg">
          <div className="h-1 w-16 bg-brand2 mb-6" />
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight">Serbisyong maaasahan, records na maayos.</h1>
          <p className="mt-4 opacity-90">Secure workspace for authorized barangay officials and staff. All actions are logged in the audit trail.</p>
        </div>
        <p className="relative text-xs opacity-75">{b?.address}</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm rise">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {b && <BrandLogo branding={b} size={48} testid="login-logo-mobile" />}
            <div><p className="font-display font-bold">{b?.lgu_name}</p><p className="text-sm text-slate-600">{b?.system_name}</p></div>
          </div>
          <p className="overline-label">Official sign-in</p>
          <h2 className="text-2xl font-bold mt-1 mb-6">Sign in to your workspace</h2>
          {loadErr && <div className="mb-4"><ErrorBox text={loadErr} /></div>}
          {!(site && site.kind !== "central" && !site.branding) && (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
              <input id="email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2.5" data-testid="login-email-input" />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">Password</label>
              <input id="password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2.5" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" role="alert" data-testid="login-error">{error}</p>}
            <Btn type="submit" disabled={busy} className="w-full !py-2.5" data-testid="login-submit-btn"><Lock className="h-4 w-4" />{busy ? "Signing in…" : "Sign in"}</Btn>
          </form>
          )}
          <div className="mt-8 border-t pt-6">
            <Link to={`/portal${lgu ? `?lgu=${lgu}` : ""}`} className="flex items-center gap-2 text-sm font-medium text-brand hover:underline" data-testid="login-portal-link"><Globe2 className="h-4 w-4" />Public portal: request documents, track requests, file feedback</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
